import { IconPlus, IconTrash } from '@developerz/ui';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { AddKeyInput, KeyRow, ProbeModelsInput, ProviderModelOption } from '../lib/keys';
import type { LlmPref } from '../lib/my-llm-pref';
import type { ToastState } from '../lib/toast';
import { AddKeyModal } from './AddKeyModal';
import { CardGridSkeleton } from './CardGridSkeleton';
import { ConfirmDialog } from './ConfirmDialog';
import { EmptyState } from './EmptyState';
import { KeyCard, providerLabel } from './KeyCard';
import { LlmPrefCard } from './LlmPrefCard';
import { PageHeader } from './PageHeader';
import { QueryBoundary, type QueryBoundaryProps } from './QueryBoundary';
import { RotateKeyModal } from './RotateKeyModal';
import { Toast } from './Toast';
import { Button } from './ui/Button';

// Presenter for the "My keys" route (epic #635) — pure rendering given the
// container's state + handlers. All data-fetching / mutation logic lives in the
// `MyKeys` route container (two files, two reasons to change; SOLID rule).
export type MyKeysViewProps = {
  hasSession: boolean;
  headingRef: (el: HTMLHeadingElement) => void;
  keysQuery: QueryBoundaryProps<readonly KeyRow[]>['query'];
  // add
  addOpen: boolean;
  addBusy: boolean;
  addError: string | null;
  onOpenAdd: () => void;
  onSubmitAdd: (input: AddKeyInput) => void;
  onCloseAdd: () => void;
  /** Pre-capture enumeration for the PERSONAL pool (`POST /v1/me/keys/models`). */
  onProbeModels: (input: ProbeModelsInput) => Promise<readonly ProviderModelOption[]>;
  // per-key actions
  onVerify: (row: KeyRow) => void;
  onAskRevoke: (row: KeyRow) => void;
  // revoke confirm
  revoking: KeyRow | null;
  onCloseRevoke: () => void;
  onConfirmRevoke: () => void;
  // rotate the raw secret in place (#971)
  onAskRotate: (row: KeyRow) => void;
  rotating: KeyRow | null;
  rotateBusy: boolean;
  rotateError: string | null;
  onSubmitRotate: (input: { readonly key: string }) => void;
  onCloseRotate: () => void;
  // re-rank a key in the pool (plan 102 slice 05b); absent = not offered
  onSetPriority?: ((row: KeyRow, priority: number) => void) | undefined;
  priorityBusy: (id: string) => boolean;
  // model-default preference
  pref: LlmPref | null | undefined;
  prefBusy: boolean;
  prefError: string | null;
  onSavePref: (input: { provider: string | null; model: string }) => void;
  onClearPref: () => void;
  // transient notices (add success / rotate success / revoke failure)
  toast: ToastState | null;
  onToastDismiss: () => void;
};

export function MyKeysView(props: MyKeysViewProps): JSX.Element {
  return (
    <>
      <PageHeader
        title={t('my_keys.title')}
        desc={t('my_keys.subtitle')}
        titleRef={props.headingRef}
        actions={
          <Show when={props.hasSession}>
            <Button onClick={props.onOpenAdd}>
              <IconPlus />
              {t('account_keys.add_key')}
            </Button>
          </Show>
        }
      />

      <Show when={props.hasSession} fallback={<EmptyState message={t('my_keys.no_session')} />}>
        <QueryBoundary
          query={props.keysQuery}
          isEmpty={(rows) => rows.length === 0}
          loading={<CardGridSkeleton listClass="grid-cards-3 account-keys__list" />}
          empty={<EmptyState message={t('my_keys.empty')} />}
        >
          {(rows) => (
            <ul class="grid-cards-3 account-keys__list">
              <For each={rows}>
                {(row) => (
                  <KeyCard
                    row={row}
                    onRevoke={props.onAskRevoke}
                    onVerify={props.onVerify}
                    onRotate={props.onAskRotate}
                    onSetPriority={props.onSetPriority}
                    priorityBusy={props.priorityBusy(row.id)}
                  />
                )}
              </For>
            </ul>
          )}
        </QueryBoundary>

        <LlmPrefCard
          pref={props.pref}
          busy={props.prefBusy}
          error={props.prefError}
          onSave={props.onSavePref}
          onClear={props.onClearPref}
        />

        <AddKeyModal
          open={props.addOpen}
          busy={props.addBusy}
          error={props.addError}
          probe={props.onProbeModels}
          onSubmit={props.onSubmitAdd}
          onClose={props.onCloseAdd}
        />

        <RotateKeyModal
          open={props.rotating !== null}
          provider={providerLabel(props.rotating?.provider ?? '')}
          busy={props.rotateBusy}
          error={props.rotateError}
          onSubmit={props.onSubmitRotate}
          onClose={props.onCloseRotate}
        />

        <ConfirmDialog
          open={props.revoking !== null}
          title={t('account_keys.revoke_title')}
          desc={t('account_keys.revoke_body', {
            provider: providerLabel(props.revoking?.provider ?? ''),
          })}
          media={<IconTrash />}
          cancelLabel={t('account_keys.cancel')}
          actionLabel={t('account_keys.revoke')}
          destructive
          onCancel={props.onCloseRevoke}
          onConfirm={props.onConfirmRevoke}
        />
      </Show>

      <Toast
        message={props.toast?.message ?? null}
        tone={props.toast?.tone ?? 'success'}
        action={props.toast?.action}
        onDismiss={props.onToastDismiss}
      />
    </>
  );
}
